import { safeStringify } from '../utils/utils';

import { captureException } from './sentryService';

export type BraintreeEventTypes =
  | '3DSdisplay'
  | '3DSfrictionless'
  | '3DScancel'
  | '3DSsubmitOK'
  | '3DSsubmitKO'
  | '3DSsubmitKObackend'
  | 'BTformOK'
  | 'BTformKO'
  | 'BTretryPopupErrorClose';

export type DiiEventName = 'checkout_registerBT' | 'edit_paymentBT';

type TrafficNotificationType = 'frontError' | 'backError';

export type TrafficNotification = {
  type: TrafficNotificationType;
  field: string;
  error: string;
};

interface Traffic {
  url?: string;
  channel?: string;
  domain?: string;
  locale?: string;
  page?: {
    type: string;
    category: string;
  };
  notifications?: TrafficNotification[];
}

interface TrafficWrapper {
  traffic: Traffic;
}

const diiTracking = (eventName: DiiEventName, eventPayload?: string | string[] | object) => {
  try {
    if (typeof window._satellite !== 'undefined' && typeof window._satellite.track === 'function') {
      window._satellite.track(eventName, eventPayload);
    }
  } catch (error: unknown) {
    captureException({
      error,
      message: `Failed diiTracking on event ${eventName} with payload ${safeStringify(
        eventPayload,
      )}`,
    });
  }
};

export const braintreeTracking = (
  eventType: BraintreeEventTypes,
  eventData?: string | string[],
) => {
  try {
    if (typeof window !== 'undefined' && typeof (window as any).BraintreeTracking === 'function') {
      window.BraintreeTracking(eventType, eventData);
    }
  } catch (error: unknown) {
    captureException({
      error,
      message: `Failed braintreeTracking on event ${eventType} with data ${JSON.stringify(
        eventData,
      )}`,
    });
  }
};

function isTrafficWrapper(value: any): value is TrafficWrapper {
  // eslint-disable-next-line no-prototype-builtins
  return value && value instanceof Object && value.hasOwnProperty('traffic');
}

function parseTrafficDataObject(trafficObjElement: Element | null): TrafficWrapper {
  if (trafficObjElement && trafficObjElement.innerHTML) {
    try {
      const value = JSON.parse(trafficObjElement.innerHTML);
      if (isTrafficWrapper(value)) {
        return value;
      }
    } catch (error: unknown) {
      captureException({
        error,
        message: `Failed parsing trafficDataObject ${safeStringify(trafficObjElement.innerHTML)}`,
      });
    }
  }

  return { traffic: {} };
}

export const registrationDataTrafficTracker = () => {
  const trafficObjElement = document.querySelector('#trafficDataObject');
  const trafficObjJson = parseTrafficDataObject(trafficObjElement);

  const setTrafficNotifications = (type: TrafficNotificationType, field: string, error: string) => {
    trafficObjJson.traffic.notifications = [{ type, field, error }];
  };

  const setTrackingAndUpdate = (eventName: DiiEventName) => {
    if (trafficObjElement) {
      trafficObjElement.innerHTML = JSON.stringify(trafficObjJson);
      diiTracking(eventName);
    }
  };

  const cleanTrackingNotifications = () => {
    if (trafficObjElement) {
      trafficObjJson.traffic.notifications = [];
      trafficObjElement.innerHTML = JSON.stringify(trafficObjJson);
    }
  };

  const setTrackingErrors = (
    eventName: DiiEventName,
    trafficNotifications: TrafficNotification[],
  ) => {
    trafficObjJson.traffic.notifications = trafficNotifications;
    setTrackingAndUpdate(eventName);
  };

  return {
    setTrafficNotifications,
    setTrackingAndUpdate,
    cleanTrackingNotifications,
    setTrackingErrors,
  };
};
